import { Button as MantineButton, type ButtonProps as MantineButtonProps } from '@mantine/core';
import { forwardRef } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type Variant = 'primary' | 'secondary' | 'text' | 'tertiary';
type Size = 'lg' | 'md' | 'sm' | 'xs';

type ButtonProps = Omit<MantineButtonProps, 'variant' | 'bg' | 'c' | 'size'> & {
  variant?: Variant;
  size?: Size;
  onClick?: () => void;
};

const VARIANT_PROPS: Record<Variant, Partial<MantineButtonProps>> = {
  primary: {
    variant: 'filled',
    bg: 'brand.6',
    c: 'white',
    styles: { root: { '&:active': { backgroundColor: 'var(--mantine-color-brand-8)' } } },
  },
  secondary: { variant: 'transparent', bg: 'brand.0', c: 'brand.7' },
  text: { variant: 'transparent', c: 'brand.6', fz: 'sm', p: 0 },
  tertiary: {
    variant: 'transparent',
    bg: 'white',
    c: 'brand.7',
    h: 24,
    px: 18,
    py: 4,
    styles: {
      root: {
        border: '1px solid var(--mantine-color-brand-1)',
        minHeight: 'unset',
        flexShrink: 0,
      },
    },
  },
};

const SIZE_PROPS: Record<Size, Partial<MantineButtonProps>> = {
  lg: { h: 48, px: 16, fz: 'md' },
  md: { h: 40, px: 14, fz: 'sm' },
  sm: { h: 32, px: 'sm', fz: 'sm' },
  xs: { h: 28, px: 12, fz: 'xs' },
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'lg', style, children, disabled, ...props },
  ref,
) {
  const variantProps =
    disabled && variant === 'primary'
      ? { ...VARIANT_PROPS[variant], bg: 'gray.3' as const }
      : VARIANT_PROPS[variant];

  const sizeProps = variant === 'text' ? {} : SIZE_PROPS[size];

  return (
    <MantineButton
      ref={ref}
      fw={FONT_WEIGHTS.semibold}
      {...sizeProps}
      {...variantProps}
      disabled={disabled}
      {...props}
      style={{ borderRadius: variant === 'tertiary' ? 8 : 20, ...style }}
    >
      {children}
    </MantineButton>
  );
});
